import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Layers } from "lucide-react";
import { useArticlePath } from "@/lib/i18n";
import { trackEvent } from "@/hooks/useArticleAnalytics";

type NavItem = { slug: string; title: string } | null;

/* Article end navigation (§35): real eligible stories, editorial chronology. */
export const PrevNextNav = ({ nav }: { nav?: { prev: NavItem; next: NavItem } }) => {
  const toArticle = useArticlePath();
  if (!nav || (!nav.prev && !nav.next)) return null;
  return (
    <nav aria-label="Article navigation" data-testid="article-prev-next"
      className="mt-10 grid grid-cols-2 rounded-xl border border-white/[0.08] bg-[#0a0b10] overflow-hidden relative">
      <span aria-hidden className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 hidden sm:flex h-8 w-8 items-center justify-center rounded-full border border-white/[0.1] bg-[#0d0d14] z-10">
        <Layers className="h-3.5 w-3.5 text-[#7C3AED]" />
      </span>
      {nav.prev ? (
        <Link to={toArticle(nav.prev.slug)} data-testid="article-nav-prev"
          onClick={() => trackEvent("article_nav_click", { dir: "prev", slug: nav.prev!.slug })}
          className="group p-4 sm:p-5 border-r border-white/[0.06] hover:bg-white/[0.03] transition-colors">
          <p className="flex items-center gap-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.24em] text-gray-500 group-hover:text-[#00E5FF] transition-colors">
            <ArrowLeft className="h-3 w-3" aria-hidden /> Previous Story
          </p>
          <p className="mt-2 text-[13.5px] font-semibold text-gray-200 group-hover:text-white leading-snug line-clamp-2 transition-colors">
            {nav.prev.title}
          </p>
        </Link>
      ) : (
        <span className="p-4 sm:p-5 border-r border-white/[0.06]" aria-hidden />
      )}
      {nav.next ? (
        <Link to={toArticle(nav.next.slug)} data-testid="article-nav-next"
          onClick={() => trackEvent("article_nav_click", { dir: "next", slug: nav.next!.slug })}
          className="group p-4 sm:p-5 text-right hover:bg-white/[0.03] transition-colors">
          <p className="flex items-center justify-end gap-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.24em] text-gray-500 group-hover:text-[#00E5FF] transition-colors">
            Next Story <ArrowRight className="h-3 w-3" aria-hidden />
          </p>
          <p className="mt-2 text-[13.5px] font-semibold text-gray-200 group-hover:text-white leading-snug line-clamp-2 transition-colors">
            {nav.next.title}
          </p>
        </Link>
      ) : (
        <span className="p-4 sm:p-5" aria-hidden />
      )}
    </nav>
  );
};
